// /tests/:id — the block-by-block test/worksheet editor (Module P2).
//
// RENDER-REBUILD DISCIPLINE (WEB-P1's own warning, taken seriously): a full render() throws away
// and rebuilds every input in #app, so anything typed into a focused field would vanish on the next
// keystroke if that keystroke ever triggered one. The rule this file follows throughout: typing in
// ANY input (header field, a block's own edit form, the Item Bank search box) NEVER calls render()
// — it only mutates the in-memory state object directly and, where the TOS panel depends on it,
// repaints ONLY `#tos-panel`'s innerHTML. render() itself fires only on discrete, deliberate clicks
// (add/remove/reorder a block, open/close its edit form, open/close the Item Bank picker) — actions
// that don't hold focus inside a text field to begin with, so rebuilding around them is safe. This
// sidesteps the trap by construction rather than by tracking one-off exceptions.

import { api, ApiError } from "../api";
import { registerNavigationGuard } from "../router";
import { esc } from "../dom";
import { initAutosave, type Autosave } from "../autosave";
import {
  BLOCK_TYPES, BLOCK_LABEL, blockDef, newBlock, fileToCappedDataUrl,
  type EditorBlock, type BlockType,
} from "../blocks";
import { summarize, MONTHLY_ITEM_TARGET, MONTHLY_MARK_TARGET, type TosSummary } from "../assessment-tos";
import {
  loadItemBank, filterCards, blockFromCard, bandForGrade, itemBankProvenanceForBlock,
  blocksForItemCount, allocationReasonsForCard, type ItemBankCard,
  type ItemBankAllocationRejection,
} from "../itembank";
import { FONT_EN_OPTIONS, FONT_KM_OPTIONS, DEFAULT_FONT_EN, DEFAULT_FONT_KM, PAPER_KINDS, PAPER_KIND_LABEL } from "../print-fonts";
import type { WeekClock } from "../clock";
import { createAssessmentResources, questionChooserHtml } from '../assessment-guide';

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function monthLabelOf(iso: string): string {
  const [y, m] = iso.split("-").map(Number) as [number, number];
  return `${MONTH_NAMES[m - 1]} ${y}`;
}
function shortDateOf(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return `${WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d}`;
}

interface HeaderData {
  school_name_en: string;
  school_name_km: string;
  logo_data_url: string;
  class_label: string;
  teacher_name: string;
  show_name_line: boolean;
  paper_kind: string;
  month_label: string;
  font_en: string;
  font_km: string;
}
interface AssessmentMeta {
  id: string; kind: "test" | "worksheet"; title: string; class_id: string | null;
  grade: number | null; subject: string; node_id: string | null;
  test_date: string | null; time_min: number | null; status: "draft" | "tos_ok" | "checked";
  academic_year: string | null; assessment_slot: "M1" | "M2" | "SA1" | null;
  taught_through_node: string | null;
}
/** Ruling 4 (F-2026-09-17-15): this card version has already been in front of this grade, in this
 *  academic year, on a lesson worksheet. A WARNING for the assembler, never a reason to refuse. */
interface WorksheetExposure {
  card_id: string;
  card_version_sha256: string;
  grade: number;
  academic_year: string;
  node_id: string | null;
}
interface AllocationState {
  context: { use_kind?: string; academic_year?: string } | null;
  uses: Array<{ card_id: string; card_version_sha256: string; confirmed_use: number; academic_year: string; assessment_slot: string | null }>;
  rejections: ItemBankAllocationRejection[];
  exposures?: WorksheetExposure[];
  reuse_allowed_from_year: 4;
}
/** "G8-T1-10" -> "G8 Term 1 week 10". Ruling 4 asks the warning to name the grade and the week, so
 *  the node id is spelled out rather than printed raw at a teacher. */
function weekLabel(nodeId: string | null): string {
  const match = /^G(10|[1-9])-T([1-3])-(\d{2})$/.exec(nodeId ?? "");
  return match ? `G${match[1]} Term ${match[2]} week ${Number(match[3])}` : "";
}
function exposureLine(list: WorksheetExposure[]): string {
  const first = list[0];
  if (!first) return "";
  const where = list.map((e) => weekLabel(e.node_id) || `G${e.grade} (week not recorded)`);
  const unique = [...new Set(where)];
  return `Already used on a lesson worksheet in ${first.academic_year}: ${unique.join(", ")}. `
    + `That does not block this paper — you decide whether the class has seen it too recently.`;
}
interface AssessmentResponse {
  assessment: AssessmentMeta & Record<string, unknown>;
  header: HeaderData;
  blocks: Array<{ id: string; type: BlockType; marks: number; bloom: "k" | "c" | "a" | null; difficulty: "E" | "M" | "D" | null; content: Record<string, unknown> }>;
  total_marks: number;
  can_edit: boolean;
  can_check: boolean;
  allocation: AllocationState;
}

/** Whether any term is on record, for a worksheet whose classroom use the server could not date.
 *  `unknown` when the terms could not be read (offline, a server error): the line then claims
 *  nothing about them. */
type TermNote = "none" | "some" | "unknown";

/** GET /api/terms/current answers with a term whenever one exists (the current one, else the most
 *  recent already started, else the first: functions/api/_lib/db.ts getTermForDate) and 404 only
 *  when none does. Team-readable, so a teacher's editor can ask it. */
async function readTermNote(): Promise<TermNote> {
  try {
    await api("/api/terms/current");
    return "some";
  } catch (e) {
    return e instanceof ApiError && e.status === 404 ? "none" : "unknown";
  }
}

/** The worksheet's Item Bank line when it has a grade but no dated classroom use (allocation.context
 *  null). Only the words change: no card offers Insert on a null context, whatever this says. With
 *  any term on record the server dates a classroom use from the nearest term (even before the first
 *  one starts), so a null context there is never the calendar's doing: the line names the grade
 *  when the grade is the cause, says so when no term exists, and otherwise claims nothing about
 *  terms. */
function undatedUseLine(grade: number, note: TermNote | null): string {
  // worksheetContext's invalid_grade rule (functions/api/_lib/assessment-blocks.ts): a whole number
  // from 1 to 10.
  if (!Number.isInteger(grade) || grade < 1 || grade > 10) {
    return `Item Bank cards are for grades 1 to 10, so none can be inserted on a Grade ${grade} worksheet.`;
  }
  return note === "none"
    ? "No term is on record, so a classroom use cannot be dated and no card can be inserted. Ask the Lead to add this academic year's terms."
    : "A classroom use cannot be dated for this worksheet yet, so no card can be inserted.";
}

const BLOOM_LABEL: Record<string, string> = { k: "Knowledge", c: "Comprehension", a: "Application" };
const DIFF_LABEL: Record<string, string> = { E: "Easy", M: "Medium", D: "Difficult" };

export async function renderTestEditor(app: HTMLElement, params: Record<string, string>): Promise<void> {
  await import('../styles/assessment-guide.css');
  const id = params.id!;
  const data = await api<AssessmentResponse>(`/api/assessments/${encodeURIComponent(id)}`);
  const canEdit = data.can_edit;

  const meta: AssessmentMeta = {
    id: data.assessment.id, kind: data.assessment.kind, title: data.assessment.title,
    class_id: data.assessment.class_id, grade: data.assessment.grade, subject: data.assessment.subject,
    node_id: data.assessment.node_id, test_date: data.assessment.test_date, time_min: data.assessment.time_min,
    status: data.assessment.status,
    academic_year: typeof data.assessment.academic_year === "string" ? data.assessment.academic_year : null,
    assessment_slot: data.assessment.assessment_slot === "M1" || data.assessment.assessment_slot === "M2" || data.assessment.assessment_slot === "SA1"
      ? data.assessment.assessment_slot : null,
    taught_through_node: typeof data.assessment.taught_through_node === "string" ? data.assessment.taught_through_node : null,
  };
  const header: HeaderData = { ...data.header };
  let blocks: EditorBlock[] = data.blocks.map((b) => ({ id: b.id, type: b.type, marks: b.marks, bloom: b.bloom, difficulty: b.difficulty, content: b.content }));
  // A worksheet whose classroom use cannot be dated says whether any term is on record
  // (undatedUseLine); read once, alongside the class list. A test never shows that line.
  const termNoteRead: Promise<TermNote | null> = meta.kind === "worksheet" ? readTermNote() : Promise.resolve(null);
  let classes: Array<{ id: string; name: string }> = [];
  try { classes = (await api<{ classes: Array<{ id: string; name: string }> }>("/api/classes?scope=own")).classes; } catch { /* teacher list is best-effort here */ }

  // The clock (P1b Job 3): when this grade has an upcoming test and the paper's own date is still
  // blank, pre-fill it — a head start, never a gate; the teacher's own typed value is never
  // overwritten (see below, checked BEFORE mutating meta). A calendar move after the paper was
  // dated shows as a quiet "Calendar says …" chip instead of silently changing what was typed.
  let clock: WeekClock | null = null;
  if (meta.grade && meta.kind === "test") {
    clock = await api<WeekClock | { clock: null }>(`/api/clock?grade=${meta.grade}`)
      .then((c) => ("week_no" in c ? c : null))
      .catch(() => null);
  }
  if (clock?.next_test && !meta.test_date) {
    meta.test_date = clock.next_test.date;
    if (!header.month_label) header.month_label = monthLabelOf(clock.next_test.date);
  }
  const calendarSaysDate =
    clock?.next_test && meta.test_date && clock.next_test.date !== meta.test_date ? clock.next_test.date : null;
  const termNote = await termNoteRead;
  const linkedLessonId=typeof data.assessment.lesson_id==='string'?data.assessment.lesson_id:undefined;
  const linkedSession=linkedLessonId?await api<{lesson:{term_id:string;week_no:number}}>(`/api/lessons/${encodeURIComponent(linkedLessonId)}/session`).catch(()=>null):null;
  const resourceBrowser = await createAssessmentResources(meta.node_id, meta.grade, {
    lessonId: linkedLessonId, classId: meta.class_id ?? undefined,
    termId:linkedSession?.lesson.term_id,weekNo:linkedSession?.lesson.week_no,
  });

  let editingId: string | null = null;
  let pickerOpen = false;
  let pickerBand = bandForGrade(meta.grade) ?? "";
  let pickerQuery = "";
  let itemBankCards: ItemBankCard[] = [];
  let allocationState: AllocationState = data.allocation;
  let err: string | null = null;

  function currentStatus(): AssessmentMeta["status"] {
    if (meta.status === "checked") return "checked";
    return summarize(blocksForItemCount(blocks), { grade: meta.grade, kind: meta.kind }).passesGate ? "tos_ok" : "draft";
  }

  function snapshot(): string {
    return JSON.stringify({
      title: meta.title, class_id: meta.class_id, grade: meta.grade, subject: meta.subject,
      node_id: meta.node_id, test_date: meta.test_date, time_min: meta.time_min,
      academic_year: meta.academic_year, assessment_slot: meta.assessment_slot,
      taught_through_node: meta.taught_through_node,
      header, status: currentStatus(),
      blocks: blocks.map((b) => ({ id: b.id, type: b.type, marks: b.marks, bloom: b.bloom, difficulty: b.difficulty, content: b.content })),
    });
  }
  let saved = snapshot();

  async function persist(keepalive: boolean): Promise<boolean> {
    const s = snapshot();
    if (s === saved) return false;
    const body = JSON.parse(s);
    const response = await api<{ ok: true; allocation?: AllocationState }>(`/api/assessments/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body), keepalive });
    if (response.allocation) allocationState = response.allocation;
    meta.status = body.status;
    saved = s;
    return true;
  }

  // Two save paths, deliberately different (see the file header's render-rebuild discipline):
  // `autosave.schedule()` for continuous typing — debounced, shows "Saving…/Saved" — is ONLY safe
  // between renders, since initAutosave's own staleness guard disposes itself the instant its
  // indicator element is detached (autosave.ts's header comment), which EVERY render() does. So any
  // action immediately followed by render() calls `saveNow()` instead: an immediate, un-debounced
  // persist() that touches no DOM, taken before the old indicator (and its autosave instance) is
  // torn down. `mountAutosave()` then re-binds a fresh instance to the new indicator at the end of
  // every render() — cheap, and there is never a pending debounce to lose by disposing the old one,
  // because saveNow() already flushed whatever was dirty first.
  let autosave: Autosave;
  function mountAutosave(): void {
    autosave?.dispose();
    autosave = initAutosave({ indicator: app.querySelector<HTMLElement>("#autosave")!, save: persist });
  }
  function saveNow(): void {
    void persist(false).catch((ex) => {
      err = (ex as Error).message;
      render();
    });
  }

  // ── the TOS panel: the one region that must repaint on every keystroke that touches marks/Bloom,
  //    WITHOUT a full render() (see file header). ─────────────────────────────────────────────
  function tosPanelHtml(): string {
    if (!canEdit && meta.status === "draft") return ""; // nothing to show a Lead on an untouched draft
    const s: TosSummary = summarize(blocksForItemCount(blocks), { grade: meta.grade, kind: meta.kind });
    // The badge means "meets the standard" — it must never contradict the Notes list below it.
    // TOS OK only on zero validate() findings (errors AND warnings); status itself may still say
    // tos_ok on the error-free rule alone (the Lead's sign-off is separate — see currentStatus()).
    const badge = s.violations.length === 0
      ? `<span class="chip submitted">TOS OK</span>`
      : `<span class="chip">${s.violations.length} note${s.violations.length === 1 ? "" : "s"}</span>`;
    if (s.itemCount === 0 || s.totalMarks <= 0) {
      return `<div class="card"><h2 style="margin-top:0">Table of Specifications</h2><p class="hint">Add marks to a block to see the breakdown here.</p></div>`;
    }
    const untaggedNote = s.bloomUntaggedMarks > 0
      ? `<p class="hint">${s.bloomUntaggedMarks} of ${s.totalMarks} marks carry no thinking level, so this split covers only the rest.</p>`
      : "";
    const bloomRow = s.bloom
      ? (["k", "c", "a"] as const).map((k) => `<div class="tos-row"><span>${BLOOM_LABEL[k]}</span><span>${Math.round(s.bloom![k] * 100)}%<span class="muted"> · target ${k === "k" ? 50 : k === "c" ? 30 : 20}%</span></span></div>`).join("")
      : "";
    // Difficulty is reported in ITEMS, not a share, because items are the standard's own basis for
    // it (20/60/20 by item count). It is no longer blanked by a single untagged block — the
    // untagged ones are counted in their own row and named in Notes below (F-2026-09-18-07).
    const untaggedItems = s.components.reduce((sum, c) => sum + c.untagged, 0);
    const diffRow = s.difficulty
      ? s.difficulty.map((d) => `<div class="tos-row"><span>${d.label}</span><span>${d.items}<span class="muted"> · standard ${d.target}</span></span></div>`).join("")
        + (untaggedItems ? `<div class="tos-row"><span>Untagged</span><span>${untaggedItems}<span class="muted"> · standard 0</span></span></div>` : "")
      : `<p class="hint">Add a block to see this split.</p>`;
    const componentRow = s.components.length
      ? s.components.map((c) => {
          const frame = c.frame ? ` <span class="muted">· frame ${c.frame.easy}/${c.frame.medium}/${c.frame.difficult}</span>` : "";
          return `<div class="tos-row"><span>${esc(c.component)}</span><span>${c.easy}/${c.medium}/${c.difficult}${c.untagged ? ` <span class="muted">(+${c.untagged} untagged)</span>` : ""}${frame}</span></div>`;
        }).join("")
      : "";
    const violations = s.violations.length
      ? `<ul class="tos-violations">${s.violations.map((v) => `<li>${esc(v.message)}</li>`).join("")}</ul>`
      : `<p class="hint">No guideline gaps found.</p>`;
    return `<div class="card">
      <div style="display:flex;align-items:baseline;justify-content:space-between"><h2 style="margin-top:0">Table of Specifications</h2>${badge}</div>
      <div class="tos-row"><span>Marks</span><span>${s.totalMarks} <span class="muted">· target ${MONTHLY_MARK_TARGET}</span></span></div>
      <div class="tos-row"><span>Items</span><span>${s.itemCount} <span class="muted">· target ${MONTHLY_ITEM_TARGET}</span></span></div>
      ${bloomRow ? `<h3 style="font-size:var(--text-sm)">Bloom split (by mark)</h3>${bloomRow}${untaggedNote}` : untaggedNote}
      <h3 style="font-size:var(--text-sm)">Difficulty (by item)</h3>${diffRow}
      ${componentRow ? `<h3 style="font-size:var(--text-sm)">By component <span class="muted">E/M/D</span></h3>${componentRow}` : ""}
      <h3 style="font-size:var(--text-sm)">Notes</h3>${violations}
    </div>`;
  }
  function refreshTos(): void {
    const el = app.querySelector<HTMLElement>("#tos-panel");
    if (el) el.innerHTML = tosPanelHtml();
  }

  // ── the Item Bank panel, which reads differently under each of the two rules ──────────────────
  // A TEST is an M1/M2/SA1 paper: Brief 6 unchanged, three fields the teacher types, fail-closed.
  // A WORKSHEET is ordinary classroom practice under owner ruling F-2026-09-17-15 "Ruling A": it
  // has no slot and no typed year at all — both are derived server-side from the paper's term —
  // so showing those inputs would invite a teacher to set something the server rightly ignores.
  function allocationCardHtml(dis: string): string {
    if (meta.kind === "worksheet") {
      const year = allocationState.context?.academic_year;
      const state = year
        ? `This worksheet may use any accepted Item Bank card. Classroom use does not spend a card's `
          + `monthly or semester allocation — it is still recorded, against `
          + `${meta.grade ? `G${meta.grade}` : "this paper"} in academic year ${esc(String(year))}, `
          + `and an M1/M2/SA1 assembler is warned if it reaches for the same version.`
        : !meta.grade
          ? "Set this worksheet's grade before inserting an Item Bank card — the record of a classroom use names the grade it was used with."
          : undatedUseLine(meta.grade, termNote);
      return `<h3 style="font-size:var(--text-sm)">Item Bank</h3>
      <p class="hint" id="allocation-state">${esc(state)}</p>`;
    }
    return `<h3 style="font-size:var(--text-sm)">Item Bank allocation</h3>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 140px"><label>Academic year</label><input name="academic_year" value="${esc(meta.academic_year ?? "")}" placeholder="2026-27" ${dis} /></div>
        <div style="flex:1 1 120px"><label>Assessment slot</label><select name="assessment_slot" ${dis}>
          <option value="">Select…</option>${(["M1", "M2", "SA1"] as const).map((slot) => `<option value="${slot}" ${meta.assessment_slot === slot ? "selected" : ""}>${slot}</option>`).join("")}
        </select></div>
        <div style="flex:1 1 180px"><label>Taught through node</label><input name="taught_through_node" value="${esc(meta.taught_through_node ?? "")}" placeholder="G6-T1-10" ${dis} /></div>
      </div>
      <p class="hint" id="allocation-state">${allocationState.uses.length
        ? `${allocationState.uses.filter((use) => use.confirmed_use).length} confirmed · ${allocationState.uses.filter((use) => !use.confirmed_use).length} pending · confirmed versions may be reused from programme year ${allocationState.reuse_allowed_from_year}`
        : "Set all three fields before inserting an Item Bank card. Unknown years, slots and nodes fail closed."}${exposureSummaryHtml()}</p>`;
  }
  /** Ruling 4, on the paper as a whole: which of the cards already on THIS assessment were seen on
   *  a worksheet first. Appended to the panel, never as an error — the assembler decides. */
  function exposureSummaryHtml(): string {
    const list = allocationState.exposures ?? [];
    if (!list.length) return "";
    const ids = [...new Set(list.map((e) => e.card_id))];
    return `<br /><span class="ib-exposure-summary">⚠ ${ids.length} card version${ids.length === 1 ? " has" : "s have"} `
      + `already been used on a lesson worksheet this academic year: ${esc(ids.join(", "))}. Not a block — your call.</span>`;
  }

  // ── header card ──────────────────────────────────────────────────────────────────────────────
  function headerHtml(): string {
    const classOpts = `<option value="">(no class)</option>` + classes.map((c) => `<option value="${esc(c.id)}" ${meta.class_id === c.id ? "selected" : ""}>${esc(c.name)}</option>`).join("");
    const dis = canEdit ? "" : "disabled";
    return `<div class="card" id="header-card">
      <h2 style="margin-top:0">${meta.kind === "test" ? "Test" : "Worksheet"} details</h2>
      <label>Title</label><input name="title" value="${esc(meta.title)}" ${dis} />
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 120px"><label>Grade</label><input type="number" name="grade" min="1" max="12" value="${meta.grade ?? ""}" ${dis} /></div>
        <div style="flex:1 1 140px"><label>Subject</label><input name="subject" value="${esc(meta.subject)}" ${dis} /></div>
        <div style="flex:1 1 160px"><label>Class</label><select name="class_id" ${dis}>${classOpts}</select></div>
      </div>
      ${allocationCardHtml(dis)}
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 140px"><label>Date</label><input type="date" name="test_date" value="${esc(meta.test_date ?? "")}" ${dis} />
          ${calendarSaysDate ? `<span class="chip" style="margin-top:4px;display:inline-block">Calendar says ${esc(shortDateOf(calendarSaysDate))}</span>` : ""}
        </div>
        <div style="flex:1 1 140px"><label>Time (min)</label><input type="number" name="time_min" min="1" value="${meta.time_min ?? ""}" ${dis} /></div>
      </div>
      <h3 style="font-size:var(--text-sm)">Printed header</h3>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 160px"><label>Paper kind</label>
          <select name="paper_kind" ${dis}>${PAPER_KINDS.map((k) => `<option value="${k}" ${header.paper_kind === k ? "selected" : ""}>${PAPER_KIND_LABEL[k]}</option>`).join("")}</select>
        </div>
        <div style="flex:1 1 160px"><label>Month / date label</label><input name="month_label" value="${esc(header.month_label)}" placeholder="June 2026" ${dis} /></div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 200px"><label>School name (English)</label><input name="school_name_en" value="${esc(header.school_name_en)}" ${dis} /></div>
        <div style="flex:1 1 200px"><label>School name (Khmer)</label><input name="school_name_km" lang="km" value="${esc(header.school_name_km)}" ${dis} /></div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end">
        <div style="flex:1 1 200px"><label>Class label on the paper</label><input name="class_label" value="${esc(header.class_label)}" placeholder="e.g. G6A" ${dis} /></div>
        <div style="flex:1 1 200px"><label>Teacher name on the paper</label><input name="teacher_name" value="${esc(header.teacher_name)}" ${dis} /></div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <div style="flex:1 1 200px"><label>English font</label>
          <select name="font_en" ${dis}>${FONT_EN_OPTIONS.map((f) => `<option value="${f.key}" ${(header.font_en || DEFAULT_FONT_EN) === f.key ? "selected" : ""}>${esc(f.label)}</option>`).join("")}</select>
        </div>
        <div style="flex:1 1 200px"><label>Khmer font</label>
          <select name="font_km" ${dis}>${FONT_KM_OPTIONS.map((f) => `<option value="${f.key}" ${(header.font_km || DEFAULT_FONT_KM) === f.key ? "selected" : ""}>${esc(f.label)}</option>`).join("")}</select>
        </div>
      </div>
      ${canEdit ? `<label>School logo (optional, kept small)</label><input type="file" name="logo" accept="image/png,image/jpeg" />` : ""}
      ${header.logo_data_url ? `<img src="${esc(header.logo_data_url)}" style="max-width:120px;display:block;margin-top:6px;border-radius:8px" />` : ""}
    </div>`;
  }

  // ── one block card ───────────────────────────────────────────────────────────────────────────
  function blockCard(b: EditorBlock, i: number, total: number): string {
    const editing = editingId === b.id;
    const def = blockDef(b.type);
    const provenance = itemBankProvenanceForBlock(b);
    const controls = canEdit
      ? `<div class="row-actions">
          <button class="linkbtn" type="button" data-blk-act="up" ${i === 0 ? "disabled" : ""} aria-label="Move up">↑</button>
          <button class="linkbtn" type="button" data-blk-act="down" ${i === total - 1 ? "disabled" : ""} aria-label="Move down">↓</button>
          ${provenance ? "" : `<button class="linkbtn" type="button" data-blk-act="${editing ? "done" : "edit"}">${editing ? "Done" : "✎ Edit"}</button>`}
          <button class="linkbtn quiet" type="button" data-blk-act="remove">✕ Remove</button>
        </div>`
      : "";
    // Instructions and section blocks carry no marks/Bloom/difficulty (assessment-tos.ts's
    // countableBlocks excludes both) — hiding the controls here is what actually keeps the TOS
    // panel's total and the header's printed total in agreement, rather than reconciling two sums
    // after the fact (eden-cold-verifier, 2026-09-03: typing marks onto an instructions block made
    // them disagree). A section's marks are shown read-only instead (section-layout.ts's sum).
    const meta_ =
      b.type === "instructions"
        ? ""
        : b.type === "section"
          ? `<div class="muted" style="font-size:var(--text-sm)">Marks print as the sum of the blocks that follow.</div>`
          : canEdit && !provenance
          ? `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px">
              <div style="flex:0 1 90px"><label style="font-size:var(--text-sm)">Marks</label><input type="number" min="0" step="0.5" name="marks" value="${b.marks}" /></div>
              <div style="flex:0 1 160px"><label style="font-size:var(--text-sm)">Thinking skill</label>
                <select name="bloom"><option value="">—</option>${(["k", "c", "a"] as const).map((k) => `<option value="${k}" ${b.bloom === k ? "selected" : ""}>${BLOOM_LABEL[k]}</option>`).join("")}</select>
              </div>
              <div style="flex:0 1 160px"><label style="font-size:var(--text-sm)">Difficulty</label>
                <select name="difficulty"><option value="">—</option>${(["E", "M", "D"] as const).map((d) => `<option value="${d}" ${b.difficulty === d ? "selected" : ""}>${DIFF_LABEL[d]}</option>`).join("")}</select>
              </div>
            </div>`
          : `<div class="muted" style="font-size:var(--text-sm)">${b.marks} mark${b.marks === 1 ? "" : "s"}${b.bloom ? ` · ${BLOOM_LABEL[b.bloom]}` : ""}${b.difficulty ? ` · ${DIFF_LABEL[b.difficulty]}` : ""}</div>`;
    // OWNER RULING on F-2026-09-17-16, 2026-09-18. An imported block used to offer no way to edit
    // it at all — the control was simply absent, and the API refused the save even if one got
    // through. It is offered now, on its OWN row rather than beside ↑ ↓ ✕: it is not a peer of
    // those, its label has to state a consequence that cannot be undone by re-typing, and a label
    // that long in the action row pushes the page sideways at 390px (caught by screens.mjs).
    const sourceMeta = provenance
      ? `<p class="muted ib-provenance" style="font-size:var(--text-sm);margin:6px 0 0">Item Bank ${esc(provenance.card_id)} · ${esc(provenance.band)} · ${esc(provenance.component)} · ${provenance.items} item${provenance.items === 1 ? "" : "s"} · version ${esc(provenance.card_version_sha256.slice(0, 12))} · imported content is immutable while it stays the card</p>`
        + (canEdit
          ? `<div class="row-actions" style="margin-top:4px"><button class="linkbtn" type="button" data-blk-act="detach" data-ib-detach="${esc(provenance.card_id)}">✎ Edit — this stops being the card</button></div>`
          : "")
      : "";
    const body = editing ? `<form class="blk-form" data-id="${esc(b.id)}">${def.editForm(b.content)}</form>` : `<p class="muted" style="margin:6px 0 0">${esc(def.summary(b.content))}</p>`;
    return `<div class="card blk-card" data-id="${esc(b.id)}">
      <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px">
        <strong>${i + 1}. ${BLOCK_LABEL[b.type]}</strong>
        ${controls}
      </div>
      ${meta_}
      ${sourceMeta}
      ${body}
    </div>`;
  }

  // ── Item Bank picker ─────────────────────────────────────────────────────────────────────────
  function pickerListHtml(): string {
    const cards = filterCards(itemBankCards, { band: pickerBand || undefined, grade: meta.grade, query: pickerQuery })
      .sort((a,b)=>Number(b.selectable)-Number(a.selectable));
    if (!itemBankCards.length) return `<p class="hint">Loading the Item Bank…</p>`;
    if (!cards.length) return `<p class="empty">No cards match.</p>`;
    const ready = cards.filter(c=>c.selectable && allocationState.context!==null && allocationReasonsForCard(c,allocationState.rejections).length===0).length;
    return `<p class="hint" data-bank-ready="${ready}">${ready ? `${ready} ready-made card${ready===1?'':'s'} available for this paper. Open a preview before inserting.` : 'No ready-made cards are available for this paper yet. Browse the lesson materials above and add your own questions. The reference cards below explain what is missing.'}</p>`+cards.slice(0, 60).map((c) => {
      const allocationReasons = allocationReasonsForCard(c, allocationState.rejections);
      const available = c.selectable && allocationState.context !== null && allocationReasons.length === 0;
      const reasons = [...c.rejection_reasons, ...allocationReasons.map((reason) => reason.code)];
      // Ruling 4: a worksheet exposure is shown BESIDE an available card, never instead of it. The
      // Insert button stays live — the decision is the assembler's, and this is only the fact.
      const exposed = (allocationState.exposures ?? []).filter((e) => e.card_version_sha256 === c.card_version_sha256);
      const preview = c.selectable ? blockFromCard(c, { grade: meta.grade, rejection: "return" }) : null;
      return `
      <div class="card" style="padding:var(--space-3)">
        <div style="display:flex;justify-content:space-between;gap:8px;align-items:baseline">
          <strong style="font-size:var(--text-sm)">${esc(c.title)}</strong>
          ${available
            ? `<button class="linkbtn" type="button" data-ib-insert="${esc(c.id)}">+ Insert</button>`
            : `<span class="chip" data-ib-rejected="${esc(c.id)}">Unavailable</span>`}
        </div>
        <div class="muted" style="font-size:var(--text-sm)">${esc(c.id)} · ${esc(c.band ?? "unsupported")} · ${esc(c.component)} · ${esc(c.skill_task_family)} · ${c.marks} mark${c.marks === 1 ? "" : "s"} · ${c.items} item${c.items === 1 ? "" : "s"}</div>
        ${available ? "" : `<p class="hint ib-rejection-reasons" style="margin:4px 0 0">${esc((reasons.length ? reasons : ["allocation_context_missing"]).join(", ").replaceAll("_", " "))}</p>`}
        ${exposed.length ? `<p class="hint ib-worksheet-exposure" data-ib-exposed="${esc(c.id)}" style="margin:4px 0 0">⚠ ${esc(exposureLine(exposed))}</p>` : ""}
        ${preview?.ok ? `<details class="ib-preview"><summary>Preview questions</summary>${blockDef(preview.block.type).renderStudent(preview.block.content)}</details>` : ""}
      </div>`;
    }).join("");
  }
  function pickerHtml(): string {
    if (!pickerOpen) return "";
    const bands = ["starters", "movers", "flyers", "key", "pet"];
    return `<div class="card" id="itembank-picker">
      <div style="display:flex;justify-content:space-between;align-items:baseline"><h2 style="margin-top:0">Insert from Item Bank</h2>
        <button class="linkbtn quiet" type="button" id="ib-close">Close</button></div>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <select id="ib-band" style="flex:0 1 140px"><option value="">All bands</option>${bands.map((b) => `<option value="${b}" ${pickerBand === b ? "selected" : ""}>${b}</option>`).join("")}</select>
        <input id="ib-query" placeholder="Search title, task family, set…" value="${esc(pickerQuery)}" style="flex:1 1 200px" />
      </div>
      <p class="hint">This grade's explicit band is selected. Unavailable cards stay visible with their evidence reason; only complete cards can be inserted. Keys and scripts remain teacher-only.</p>
      <div id="itembank-list">${pickerListHtml()}</div>
    </div>`;
  }

  function render(): void {
    const printHref = `/tests/${encodeURIComponent(id)}/print`;
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";
    const checkBtn = data.can_check
      ? meta.status === "checked"
        ? `<button class="linkbtn quiet" type="button" id="uncheck">Re-open (undo sign-off)</button>`
        : `<button class="btn secondary" type="button" id="check" style="width:auto">Mark as checked</button>`
      : "";
    app.innerHTML = `
      <div class="topbar">
        <nav class="breadcrumb" aria-label="Breadcrumb"><a data-link href="/tests">Worksheets &amp; tests</a><span class="sep" aria-hidden="true">›</span><span class="current" aria-current="page">Edit</span></nav>
        <span style="display:flex;align-items:center;gap:12px">
          <span id="autosave" class="muted" aria-live="polite" style="font-size:var(--text-sm)"></span>
          <a href="${printHref}" target="_blank" rel="noopener" class="pill-btn">Print preview</a>
        </span>
      </div>
      <h1>${esc(meta.title)}</h1>
      ${errHtml}
      <p class="ag-workflow"><span>1. Find materials</span><span>→ 2. Add questions</span><span>→ 3. Check answers and print</span></p>
      <div class="ag-layout">${resourceBrowser.html()}<section>
      ${canEdit ? questionChooserHtml() : ''}
      <h2 class="ag-workspace-title">Your questions and sections</h2>
      <div id="blocklist">${blocks.length ? blocks.map((b, i) => blockCard(b, i, blocks.length)).join("") : `<p class="empty">Choose a question type above, or browse ready-made questions below.</p>`}</div>
      ${canEdit ? `<div class="card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">
          <select id="add-type">${BLOCK_TYPES.map((t) => `<option value="${t}">${BLOCK_LABEL[t]}</option>`).join("")}</select>
          <button class="btn secondary" type="button" id="add-block" style="width:auto">+ Add selected type</button>
          <button class="linkbtn" type="button" id="open-picker">Browse ready-made questions</button>
        </div>` : ""}
      ${pickerHtml()}
      </section></div>
      <details class="ag-paper-details"><summary>Paper details, class and school heading</summary>${headerHtml()}</details>
      <h2>3. Check and print</h2><p class="hint">Check each question and its answer key, then open Print preview for the pupil sheet or separate teacher key.</p>
      <div id="tos-panel">${tosPanelHtml()}</div>
      ${checkBtn ? `<div class="card">${checkBtn}</div>` : ""}`;

    wire();
    resourceBrowser.wire(app);
    mountAutosave();
    if (editingId) app.querySelector<HTMLElement>(`.blk-card[data-id="${editingId}"]`)?.scrollIntoView({ block: "nearest" });
  }

  // OWNER RULING on F-2026-09-17-16, 2026-09-18. This used to RE-ATTACH __itemBank after the
  // teacher edited the form, which made every saved edit look like a tampered card: the content no
  // longer matched the provenance content_snapshot, so the save was refused 400
  // stale_or_tampered_item_bank_provenance. An edited block is not a tampered card — it is the
  // teacher writing — so the provenance is dropped instead of restored.
  //
  // In practice the "detach" action above has already removed it before any form can open. The
  // delete stays as an INVARIANT rather than a mechanism: whatever route reaches this function, a
  // block whose content came out of an edit form can never carry Item Bank provenance, so the
  // editor badge, the session page reader (_lib/lesson-trail.ts blockOrigin) and the recorded
  // assessment_item_use row cannot drift apart.
  function updateBlockContentLive(blkEl: HTMLElement, b: EditorBlock): void {
    const form = blkEl.querySelector<HTMLFormElement>(".blk-form");
    if (form) {
      b.content = blockDef(b.type).readForm(form);
      delete b.content.__itemBank;
    }
    autosave.schedule();
    refreshTos();
  }

  function wire(): void {
    if (!canEdit) {
      app.querySelector("#check")?.addEventListener("click", () => void doCheck(true));
      app.querySelector("#uncheck")?.addEventListener("click", () => void doCheck(false));
      return;
    }
    const hcard = app.querySelector<HTMLElement>("#header-card")!;
    hcard.addEventListener("input", (e) => {
      const t2 = e.target as HTMLInputElement;
      if (!t2.name) return;
      if (t2.name === "title") meta.title = t2.value;
      else if (t2.name === "grade") meta.grade = t2.value ? Number(t2.value) : null;
      else if (t2.name === "subject") meta.subject = t2.value;
      else if (t2.name === "test_date") meta.test_date = t2.value || null;
      else if (t2.name === "time_min") meta.time_min = t2.value ? Number(t2.value) : null;
      else if (t2.name === "academic_year") meta.academic_year = t2.value || null;
      else if (t2.name === "taught_through_node") meta.taught_through_node = t2.value || null;
      else if (t2.name === "school_name_en") header.school_name_en = t2.value;
      else if (t2.name === "school_name_km") header.school_name_km = t2.value;
      else if (t2.name === "class_label") header.class_label = t2.value;
      else if (t2.name === "teacher_name") header.teacher_name = t2.value;
      else if (t2.name === "month_label") header.month_label = t2.value;
      else return;
      autosave.schedule();
    });
    hcard?.addEventListener("change", (e) => {
      const t2 = e.target as HTMLSelectElement;
      if (t2.name === "class_id") { meta.class_id = t2.value || null; autosave.schedule(); }
      else if (t2.name === "assessment_slot") { meta.assessment_slot = (t2.value || null) as AssessmentMeta["assessment_slot"]; autosave.schedule(); }
      else if (t2.name === "paper_kind") { header.paper_kind = t2.value; autosave.schedule(); }
      else if (t2.name === "font_en") { header.font_en = t2.value; autosave.schedule(); }
      else if (t2.name === "font_km") { header.font_km = t2.value; autosave.schedule(); }
    });
    hcard?.querySelector<HTMLInputElement>('input[name="logo"]')?.addEventListener("change", async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      header.logo_data_url = await fileToCappedDataUrl(file);
      saveNow();
      render();
    });

    const list = app.querySelector<HTMLElement>("#blocklist")!;
    list.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-blk-act]");
      if (!btn) return;
      const card = btn.closest<HTMLElement>(".blk-card")!;
      const bid = card.dataset.id!;
      const idx = blocks.findIndex((b) => b.id === bid);
      const act = btn.dataset.blkAct;
      if (act === "add-opt") {
        e.preventDefault();
        const b = blocks.find((x) => x.id === bid)!;
        const opts = Array.isArray(b.content.options) ? (b.content.options as string[]) : [];
        if (opts.length < 4) { b.content.options = [...opts, ""]; saveNow(); render(); }
      } else if (act === "up" && idx > 0) { [blocks[idx - 1], blocks[idx]] = [blocks[idx]!, blocks[idx - 1]!]; saveNow(); render(); }
      else if (act === "down" && idx < blocks.length - 1) { [blocks[idx + 1], blocks[idx]] = [blocks[idx]!, blocks[idx + 1]!]; saveNow(); render(); }
      else if (act === "detach") {
        // One discrete click, so render() is safe here (see the RENDER-REBUILD note at the top).
        // The provenance goes FIRST and the paper is saved immediately, so the release of this
        // card's assessment_item_use row does not wait for whatever the teacher types next: the
        // badge, the session page and the recorded use all change together, at this click.
        const b = blocks.find((x) => x.id === bid)!;
        delete b.content.__itemBank;
        editingId = bid;
        saveNow();
        render();
      }
      else if (act === "edit") { editingId = bid; render(); }
      else if (act === "done") { editingId = null; saveNow(); render(); }
      else if (act === "remove") { blocks = blocks.filter((b) => b.id !== bid); if (editingId === bid) editingId = null; saveNow(); render(); }
    });
    // Marks / Bloom / Difficulty — live, no re-render (numbers/selects don't hold typed focus the
    // way a textarea does, but skipping the rebuild keeps an open edit form's own inputs untouched).
    list.addEventListener("input", (e) => {
      const t2 = e.target as HTMLInputElement;
      const card = t2.closest<HTMLElement>(".blk-card");
      if (!card) return;
      const b = blocks.find((x) => x.id === card.dataset.id);
      if (!b) return;
      if (t2.name === "marks") { b.marks = Number(t2.value) || 0; autosave.schedule(); refreshTos(); return; }
      if (t2.closest(".blk-form")) updateBlockContentLive(card, b);
    });
    list.addEventListener("change", (e) => {
      const t2 = e.target as HTMLSelectElement;
      const card = t2.closest<HTMLElement>(".blk-card");
      if (!card) return;
      const b = blocks.find((x) => x.id === card.dataset.id);
      if (!b) return;
      if (t2.name === "bloom") { b.bloom = (t2.value || null) as EditorBlock["bloom"]; autosave.schedule(); refreshTos(); return; }
      if (t2.name === "difficulty") { b.difficulty = (t2.value || null) as EditorBlock["difficulty"]; autosave.schedule(); refreshTos(); return; }
      if (t2.name === "correct" || t2.closest(".blk-form")) updateBlockContentLive(card, b);
    });
    list.querySelectorAll<HTMLInputElement>('input[type="file"][name="image"]').forEach((fileInput) => {
      fileInput.addEventListener("change", async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        const card = fileInput.closest<HTMLElement>(".blk-card")!;
        const b = blocks.find((x) => x.id === card.dataset.id)!;
        (b.content as Record<string, unknown>).imageDataUrl = await fileToCappedDataUrl(file);
        saveNow();
        render();
      });
    });

    app.querySelector("#add-block")?.addEventListener("click", () => {
      const type = ((app.querySelector("#add-type") as HTMLSelectElement | null)!.value) as BlockType;
      const b = newBlock(type);
      blocks.push(b);
      editingId = b.id;
      saveNow();
      render();
    });
    app.querySelectorAll<HTMLButtonElement>('[data-add-question]').forEach(button=>button.addEventListener('click',()=>{
      const type=button.dataset.addQuestion as BlockType;
      if(!canEdit || !BLOCK_TYPES.includes(type)) return;
      const b=newBlock(type);blocks.push(b);editingId=b.id;saveNow();render();
      app.querySelector<HTMLInputElement|HTMLTextAreaElement>(`.blk-form[data-id="${b.id}"] input, .blk-form[data-id="${b.id}"] textarea`)?.focus();
    }));
    app.querySelector("#open-picker")?.addEventListener("click", async () => {
      try { await persist(false); } catch (ex) { err = (ex as Error).message; render(); return; }
      pickerBand = bandForGrade(meta.grade) ?? "";
      pickerOpen = true;
      render();
      if (!itemBankCards.length) {
        const idx = await loadItemBank();
        itemBankCards = idx?.cards ?? [];
        const l = app.querySelector<HTMLElement>("#itembank-list");
        if (l) l.innerHTML = pickerListHtml();
      }
    });
    app.querySelector("#ib-close")?.addEventListener("click", () => { pickerOpen = false; render(); });
    app.querySelector("#ib-band")?.addEventListener("change", (e) => {
      pickerBand = (e.target as HTMLSelectElement).value;
      app.querySelector<HTMLElement>("#itembank-list")!.innerHTML = pickerListHtml();
    });
    app.querySelector("#ib-query")?.addEventListener("input", (e) => {
      pickerQuery = (e.target as HTMLInputElement).value;
      app.querySelector<HTMLElement>("#itembank-list")!.innerHTML = pickerListHtml();
    });
    app.querySelector("#itembank-list")?.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-ib-insert]");
      if (!btn) return;
      const card = itemBankCards.find((c) => c.id === btn.dataset.ibInsert);
      if (!card) return;
      const allocationReasons = allocationReasonsForCard(card, allocationState.rejections);
      if (allocationState.context === null || allocationReasons.length) {
        err = `${card.id} is unavailable: ${(allocationReasons.length ? allocationReasons.map((reason) => reason.code) : ["allocation_context_missing"]).join(", ").replaceAll("_", " ")}`;
        render();
        return;
      }
      const result = blockFromCard(card, { grade: meta.grade, rejection: "return" });
      if (!result.ok) {
        err = `${result.card_id} is unavailable: ${result.reasons.join(", ").replaceAll("_", " ")}`;
        render();
        return;
      }
      blocks.push(result.block);
      err = null;
      saveNow();
      render();
    });

    app.querySelector("#check")?.addEventListener("click", () => void doCheck(true));
    app.querySelector("#uncheck")?.addEventListener("click", () => void doCheck(false));
  }

  async function doCheck(on: boolean): Promise<void> {
    try {
      await api(`/api/assessments/${encodeURIComponent(id)}/checked`, { method: on ? "POST" : "DELETE" });
      const refreshed = await api<AssessmentResponse>(`/api/assessments/${encodeURIComponent(id)}`);
      meta.status = refreshed.assessment.status;
      allocationState = refreshed.allocation;
      saved = snapshot();
      err = null;
    } catch (ex) {
      err = (ex as Error).message;
    }
    render();
  }

  registerNavigationGuard(app,async()=>{
    if(!canEdit||snapshot()===saved)return true;
    try{await persist(false);return snapshot()===saved;}
    catch(error){err=`Couldn’t save. Your changes are still here: ${(error as Error).message}`;render();app.querySelector('[role="alert"],.error')?.scrollIntoView({block:'center'});return false;}
  });
  window.addEventListener('beforeunload',event=>{if(app.isConnected&&canEdit&&snapshot()!==saved){event.preventDefault();event.returnValue='';}});
  render(); // first paint creates #autosave; render()'s own mountAutosave() binds to it
}
